import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { subscriptionGateway } from '../index';
import { notifySubscriptionChanged } from '../subscriptionChanged';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { BillingContext, type BillingContextValue } from '../billing/BillingContext';
import { BillingContactSection } from '../billing/BillingContactSection';
import { HistorySection } from '../billing/HistorySection';
import { InvoicesSection } from '../billing/InvoicesSection';
import { MockBillingDemoControls, MockBillingNotice } from '../billing/MockBilling';
import { PaymentMethodSection } from '../billing/PaymentMethodSection';
import { PlanSection } from '../billing/PlanSection';
import { SummarySection } from '../billing/SummarySection';
import { SupportSection } from '../billing/SupportSection';
import { BILLING_UNAVAILABLE_MESSAGE } from '../billing/billingErrors';
import { useResource } from '../billing/useResource';
import '../billing.css';

/**
 * EPN-105 — `/settings/billing`, the billing center (EPN-80's Billing Management screen, extended). Reachable in every
 * subscription state — a trial that ended, a failed payment and a canceled subscription are exactly when it is needed.
 *
 * Everything shown is what the server returned; each section loads on its own with honest loading / empty / error /
 * retry states, so one failing lookup never blanks the rest and a failure is never shown as "nothing here". The page
 * has no permission list (the browser has none): a billing write the server refuses with a plain 403 switches it to a
 * sticky read-only view for the rest of its lifetime, as EPN-80 established — the server's 403 is authoritative.
 */
export function BillingCenterPage() {
  const [params] = useSearchParams();
  const initialChangePlan = params.get('changePlan');

  const loadSubscription = useCallback((signal: AbortSignal) => subscriptionGateway.getCurrentSubscription(signal), []);
  const loadStatus = useCallback((signal: AbortSignal) => subscriptionGateway.getBillingStatus(signal), []);
  const loadPlans = useCallback(async (signal: AbortSignal) => (await subscriptionGateway.listPlans(signal)).slice().sort((a, b) => a.tier - b.tier), []);

  const subscription = useResource(loadSubscription, 'Could not load your subscription.');
  const status = useResource(loadStatus, 'Could not load your billing status.');
  const plans = useResource(loadPlans, 'Could not load the plans.');

  const [readOnly, setReadOnly] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const { reload: reloadSubscription } = subscription;
  const { reload: reloadStatus } = status;
  const refresh = useCallback(() => {
    setRefreshToken((n) => n + 1);
    reloadSubscription();
    reloadStatus();
    notifySubscriptionChanged();
  }, [reloadSubscription, reloadStatus]);

  const providerConfigured =
    status.state.status === 'unavailable' ? false
      : status.state.status === 'ready' ? status.state.data.provider.configured
        : subscription.state.status === 'ready' && subscription.state.data?.provider ? subscription.state.data.provider.configured
          : true;

  const context = useMemo<BillingContextValue>(
    () => ({ readOnly, markReadOnly: () => setReadOnly(true), providerAvailable: providerConfigured, refreshToken, refresh }),
    [readOnly, providerConfigured, refreshToken, refresh],
  );

  const providerMode = status.state.status === 'ready' ? status.state.data.provider.mode : null;

  return (
    <BillingContext.Provider value={context}>
      <section className="card card--wide billing-center" aria-labelledby="billing-management-heading">
        <div className="section-header">
          <h2 id="billing-management-heading">Billing Management</h2>
          <Link to="/settings/subscription">Compare plans</Link>
        </div>

        <p className="muted">
          Billing here covers your Escochex software subscription only. It is separate from payroll funding and employee payments.
        </p>

        <MockBillingNotice />
        {providerMode === 'test' && (
          <p className="billing-notice billing-notice--info" role="note">
            <strong>Test mode.</strong> Payments use test cards, so no real money moves.
          </p>
        )}

        {!providerConfigured && (
          <p className="billing-notice billing-notice--warning" role="status" data-testid="billing-unavailable">
            <span aria-hidden="true">⚠</span> {BILLING_UNAVAILABLE_MESSAGE} You can still see your plan and contact billing support at info@escochecks.com.
          </p>
        )}

        {readOnly && (
          <p className="read-only-notice" role="status" data-testid="billing-read-only">
            You have read-only access to billing — changes must be made by a user with billing-management permission.
          </p>
        )}

        {subscription.state.status === 'loading' && <p className="muted" role="status">Loading your billing information…</p>}
        {subscription.state.status === 'denied' && <AccessDeniedNotice requiredPermissionLabel="Subscriptions: View" />}
        {subscription.state.status === 'error' && <RetryableError message={subscription.state.message} onRetry={subscription.reload} />}

        {subscription.state.status === 'ready' && (
          <>
            <SummarySection
              subscription={subscription.state.data}
              status={status.state.status === 'ready' ? status.state.data : null}
              plans={plans.state.status === 'ready' ? plans.state.data : []}
            />
            {status.state.status === 'error' && <RetryableError message={status.state.message} onRetry={status.reload} />}

            <PaymentMethodSection
              subscription={subscription.state.data}
              status={status.state.status === 'ready' ? status.state.data : null}
            />

            {plans.state.status === 'error' && <RetryableError message={plans.state.message} onRetry={plans.reload} />}
            {plans.state.status === 'ready' && (
              <PlanSection
                subscription={subscription.state.data}
                plans={plans.state.data}
                initialChangePlanCode={initialChangePlan}
              />
            )}
          </>
        )}

        <InvoicesSection />
        <HistorySection />
        <BillingContactSection />
        <SupportSection />
        <MockBillingDemoControls onChanged={refresh} />
      </section>
    </BillingContext.Provider>
  );
}
